/**
 * Dense building blocks for the tournament setup screens (#2691), matching the
 * approved "Tournament setup, simplified" frames: 40px card headers, 12px
 * labels, 34px inputs, Slate + Emerald, Hanken Grotesk, Space Mono numbers.
 * Colours come from the shell's Slate + Emerald tokens and the theme's
 * semantic roles; the font is the theme's.
 */
import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from "react";
import { forwardRef } from "react";
import { SHELL_ACCENT, SHELL_BAR, SHELL_SURFACE } from "@bw/shared/components";

import type { Band } from "@/libs/tournamentSetupApi";

/** The theme's staff font (createBusinessTheme), inherited: setup text never names a font. */
export const STAFF_FONT = "inherit";
export const MONO_FONT = '"Space Mono", ui-monospace, monospace';

const role = (name: string) => `var(--bw-palette-semantic-${name})`;
const tint = (color: string, percent: number) => `color-mix(in srgb, ${color} ${percent}%, ${SHELL_SURFACE.surface})`;
const veil = (color: string, percent: number) => `color-mix(in srgb, ${color} ${percent}%, transparent)`;

/** Shell tokens for neutrals and the emerald accent; theme semantic roles for
 * bands and notes. No local colour values (material boundaries). */
export const tone = {
  ink: SHELL_SURFACE.ink,
  ink2: SHELL_SURFACE.ink2,
  muted: SHELL_SURFACE.ink3,
  subtle: SHELL_SURFACE.muted,
  faint: SHELL_SURFACE.muted2,
  faintLine: SHELL_SURFACE.sheetHandle,
  line: SHELL_SURFACE.line,
  divider: SHELL_SURFACE.line2,
  page: "var(--bw-palette-background-default)",
  chip: SHELL_SURFACE.fill,
  surface: SHELL_SURFACE.surface,
  hover: veil(SHELL_SURFACE.ink, 4),
  brand: SHELL_ACCENT.light,
  brandText: SHELL_SURFACE.activeTitle,
  brandSoft: SHELL_SURFACE.activeTint,
  brandMark: SHELL_BAR.avatarEmerald,
  brandNote: tint(SHELL_ACCENT.light, 5),
  brandNoteLine: tint(SHELL_ACCENT.light, 30),
  brandNoteText: SHELL_BAR.avatarEmeraldText,
  brandRing: veil(SHELL_ACCENT.light, 15),
  brandHi: tint(SHELL_ACCENT.light, 55),
  onInk: SHELL_BAR.avatarFallbackText,
  onInkStrong: SHELL_BAR.strong,
  onInkLink: SHELL_BAR.accentSoft,
  warnText: role("warning"),
  warnSoft: tint(role("warning"), 6),
  warnLine: tint(role("warning"), 30),
  warnNoteText: role("pending"),
  warnBar: role("warning"),
  badText: role("error"),
  badSoft: tint(role("error"), 6),
  badLine: tint(role("error"), 25),
  badNoteText: role("error"),
  badBar: role("error"),
  newSoft: tint(role("focus"), 8),
  newText: role("focus"),
  segmentShadow: SHELL_SURFACE.segmentShadow,
  popShadow: SHELL_SURFACE.popShadow,
  raisedShadow: `0 -4px 12px ${veil(SHELL_SURFACE.ink, 4)}`,
} as const;

export const bandText: Record<Band, string> = {
  plenty: tone.brandText,
  tight: tone.warnText,
  wont_fit: tone.badText,
};
export const bandBar: Record<Band, string> = {
  plenty: tone.brand,
  tight: tone.warnBar,
  wont_fit: tone.badBar,
};
export const bandSoft: Record<Band, string> = {
  plenty: tone.brandSoft,
  tight: tone.warnSoft,
  wont_fit: tone.badSoft,
};

/** Root for every setup surface: the staff font and base text style. */
export function SetupSurface({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) {
  return (
    <Box sx={[{ fontFamily: STAFF_FONT, color: tone.ink, fontSize: 13 }, ...(Array.isArray(sx) ? sx : [sx])]}>
      {children}
    </Box>
  );
}

export function SetupCard({
  title,
  extra,
  actions,
  children,
  sx,
  headerHeight = 40,
  id,
}: {
  title: ReactNode;
  extra?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  sx?: SxProps<Theme>;
  headerHeight?: number;
  id?: string;
}) {
  return (
    <Box
      component="section"
      id={id}
      sx={[
        { bgcolor: tone.surface, border: `1px solid ${tone.line}`, borderRadius: "10px", minWidth: 0 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        sx={{
          height: headerHeight,
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: "14px",
          borderBottom: `1px solid ${tone.divider}`,
          fontWeight: 700,
          fontSize: 13.5,
          flex: "none",
        }}
      >
        <Box component="h2" sx={{ m: 0, font: "inherit", display: "flex", alignItems: "center", gap: 1 }}>
          {title}
        </Box>
        {extra ? <Box sx={{ fontWeight: 400, fontSize: 12, color: tone.subtle }}>{extra}</Box> : null}
        <Box sx={{ flexGrow: 1 }} />
        {actions}
      </Box>
      {children}
    </Box>
  );
}

export function CardBody({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) {
  return (
    <Box sx={[{ p: "12px 14px", display: "flex", flexDirection: "column", gap: "10px" }, ...(Array.isArray(sx) ? sx : [sx])]}>
      {children}
    </Box>
  );
}

export function FieldLabel({
  htmlFor,
  children,
  sx,
  id,
}: {
  htmlFor?: string;
  children: ReactNode;
  sx?: SxProps<Theme>;
  id?: string;
}) {
  return (
    <Box
      component={htmlFor ? "label" : "span"}
      htmlFor={htmlFor}
      id={id}
      sx={[
        { fontSize: 12, fontWeight: 600, color: tone.muted, display: "flex", alignItems: "center", gap: "5px", mb: "4px" },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Box>
  );
}

const inputSx = {
  height: 34,
  boxSizing: "border-box",
  width: "100%",
  border: `1px solid ${tone.line}`,
  borderRadius: "6px",
  px: "10px",
  font: "inherit", fontWeight: 500, fontSize: 13.5,
  color: tone.ink,
  bgcolor: tone.surface,
  outline: "none",
  "&:focus": { borderColor: tone.brand, boxShadow: `0 0 0 3px ${tone.brandRing}` },
  "&:disabled": { bgcolor: tone.page, color: tone.subtle },
} as const;

export const DenseInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { mono?: boolean; sx?: SxProps<Theme> }
>(function DenseInput({ mono, sx, ...props }, ref) {
  return (
    <Box
      component="input"
      ref={ref}
      sx={[inputSx, mono ? { fontFamily: MONO_FONT, letterSpacing: "-.3px" } : {}, ...(Array.isArray(sx) ? sx : [sx])]}
      {...props}
    />
  );
});

export function DenseSelect({
  sx,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { sx?: SxProps<Theme> }) {
  return (
    <Box component="select" sx={[inputSx, { pr: "6px" }, ...(Array.isArray(sx) ? sx : [sx])]} {...props}>
      {children}
    </Box>
  );
}

type ButtonVariant = "default" | "primary" | "link";

export function DenseButton({
  variant = "default",
  size = "md",
  sx,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: "sm" | "md" | "lg";
  sx?: SxProps<Theme>;
}) {
  if (variant === "link") {
    return (
      <Box
        component="button"
        type="button"
        sx={[
          {
            background: "none",
            border: 0,
            p: 0,
            font: "inherit", fontWeight: 600, fontSize: 12.5,
            color: tone.brandText,
            cursor: "pointer",
            textDecoration: "underline",
            textUnderlineOffset: "2px",
            "&:disabled": { color: tone.faint, cursor: "default" },
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...props}
      >
        {children}
      </Box>
    );
  }
  const height = size === "sm" ? 28 : size === "lg" ? 42 : 32;
  return (
    <Box
      component="button"
      type="button"
      sx={[
        {
          height,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          px: size === "sm" ? "10px" : "12px",
          border: `1px solid ${variant === "primary" ? tone.brand : tone.line}`,
          borderRadius: "6px",
          bgcolor: variant === "primary" ? tone.brand : tone.surface,
          color: variant === "primary" ? tone.surface : tone.ink2,
          font: "inherit", fontWeight: 600, fontSize: size === "sm" ? 12.5 : size === "lg" ? 14 : 13,
          whiteSpace: "nowrap",
          cursor: "pointer",
          "&:hover": { filter: "brightness(0.97)" },
          "&:disabled": { opacity: 0.55, cursor: "default" },
          "&:focus-visible": { outline: `2px solid ${tone.brand}`, outlineOffset: "2px" },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...props}
    >
      {children}
    </Box>
  );
}

export function Pill({
  children,
  variant = "neutral",
  sx,
}: {
  children: ReactNode;
  variant?: "neutral" | "amber" | "green";
  sx?: SxProps<Theme>;
}) {
  const colors = {
    neutral: { bgcolor: tone.chip, color: tone.ink2 },
    amber: { bgcolor: tone.warnSoft, color: tone.warnText },
    green: { bgcolor: tone.brandSoft, color: tone.brandText },
  }[variant];
  return (
    <Box
      component="span"
      sx={[
        {
          display: "inline-flex",
          alignItems: "center",
          height: 20,
          px: "7px",
          borderRadius: "5px",
          fontSize: 11.5,
          fontWeight: 600,
          whiteSpace: "nowrap",
          ...colors,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Box>
  );
}

export function NewBadge() {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: 16,
        px: "5px",
        borderRadius: "4px",
        bgcolor: tone.newSoft,
        color: tone.newText,
        font: "inherit", fontWeight: 700, fontSize: 10,
        letterSpacing: ".3px",
        textTransform: "uppercase",
      }}
    >
      New
    </Box>
  );
}

export function Note({
  tone: variant,
  children,
  icon,
  sx,
  role,
}: {
  tone: "amber" | "green" | "red" | "neutral";
  children: ReactNode;
  icon?: ReactNode;
  sx?: SxProps<Theme>;
  role?: string;
}) {
  const colors = {
    amber: { bgcolor: tone.warnSoft, border: `1px solid ${tone.warnLine}`, color: tone.warnNoteText },
    green: { bgcolor: tone.brandNote, border: `1px solid ${tone.brandNoteLine}`, color: tone.brandNoteText },
    red: { bgcolor: tone.badSoft, border: `1px solid ${tone.badLine}`, color: tone.badNoteText },
    neutral: { bgcolor: tone.page, border: `1px solid ${tone.divider}`, color: tone.ink2 },
  }[variant];
  return (
    <Box
      role={role}
      sx={[
        { display: "flex", gap: 1, alignItems: "center", p: "7px 10px", borderRadius: "8px", fontSize: 12.5, lineHeight: 1.45, ...colors },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {icon}
      {children}
    </Box>
  );
}

export function Mono({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) {
  return (
    <Box
      component="span"
      sx={[{ fontFamily: MONO_FONT, fontVariantNumeric: "tabular-nums", letterSpacing: "-.3px" }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {children}
    </Box>
  );
}

export function BandBar({ band, percent, height = 8, width }: { band: Band; percent: number; height?: number; width?: number }) {
  return (
    <Box
      aria-hidden
      sx={{ height, width, borderRadius: height / 2, bgcolor: tone.chip, overflow: "hidden", mt: width ? 0 : "4px", display: width ? "inline-block" : "block" }}
    >
      <Box sx={{ height: "100%", width: `${Math.min(100, Math.round(percent))}%`, bgcolor: bandBar[band] }} />
    </Box>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  sx,
  fill,
  size = "md",
  outlined,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  label: string;
  sx?: SxProps<Theme>;
  fill?: boolean;
  /** "phone": 44 px tap targets. */
  size?: "md" | "phone";
  /** The selected option gets an emerald outline (time and rest settings, F5). */
  outlined?: boolean;
}) {
  return (
    <Box
      role="radiogroup"
      aria-label={label}
      sx={[
        { display: fill ? "flex" : "inline-flex", p: "2px", borderRadius: "7px", bgcolor: tone.chip, border: `1px solid ${tone.line}` },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {options.map((option) => {
        const on = option.value === value;
        return (
          <Box
            component="button"
            type="button"
            role="radio"
            aria-checked={on}
            key={option.value}
            onClick={() => onChange(option.value)}
            sx={{
              height: size === "phone" ? 44 : 28,
              px: "12px",
              flex: fill ? 1 : "none",
              borderRadius: "5px",
              border: outlined && on ? `1.5px solid ${tone.brand}` : 0,
              bgcolor: on ? tone.surface : "transparent",
              color: on ? tone.ink : tone.subtle,
              boxShadow: on ? tone.segmentShadow : "none",
              font: "inherit", fontWeight: 600, fontSize: 12.5,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {option.label}
          </Box>
        );
      })}
    </Box>
  );
}

export function Grow() {
  return <Box sx={{ flexGrow: 1 }} />;
}

export function SectionHeading({ children, id, sx }: { children: ReactNode; id?: string; sx?: SxProps<Theme> }) {
  return (
    <Box
      id={id}
      sx={[{ fontSize: 11, fontWeight: 700, letterSpacing: ".3px", color: tone.subtle, textTransform: "uppercase" }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {children}
    </Box>
  );
}

export function WarningIcon() {
  return (
    <svg aria-hidden="true" fill="none" height="15" stroke="currentColor" strokeLinecap="round" strokeWidth="2" viewBox="0 0 24 24" width="15">
      <path d="M12 9v4M12 17h.01M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    </svg>
  );
}

export function CheckIcon({ color = "currentColor", size = 15 }: { color?: string; size?: number }) {
  return (
    <svg aria-hidden="true" fill="none" height={size} stroke={color} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.4" viewBox="0 0 24 24" width={size}>
      <path d="M5 12l5 5L20 7" />
    </svg>
  );
}

export function LockIcon({ size = 14 }: { size?: number }) {
  return (
    <svg aria-hidden="true" fill="none" height={size} stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width={size}>
      <rect height="10" rx="2" width="14" x="5" y="11" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

export function CloseIcon() {
  return (
    <svg aria-hidden="true" fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
